import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Divider,
  useTheme,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Grid,
} from "@mui/material";
import TranslateIcon from '@mui/icons-material/Translate';
import axios from "axios";
import ProfileGameStats from "../Components/ProfileGameStats";

const FILTERS = {
  catched: { de: "Gefangen", en: "Catched" },
  shiny: { de: "Schillernd", en: "Shiny" },
  perfect: { de: "Perfekt", en: "Perfect" },
  crypto: { de: "Crypto", en: "Crypto" },
  dynamax: { de: "Dynamax", en: "Dynamax" },
  individual: { de: "Individuell", en: "Individual" },
};

const FILTER_WORDS = {
  shiny: { de: "schillernd", en: "shiny" },
  perfect: { de: "4*", en: "4*" },
  crypto: { de: "crypto,erlöst", en: "crypto,purified" },
  dynamax: { de: "dynamax,gigadynamax", en: "dynamax,gigantamax" }
};

const Profile = () => {
  const theme = useTheme();
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [pokedexStats, setPokedexStats] = useState([]);
  const [selectedGen, setSelectedGen] = useState(0);

  const [tradeMode, setTradeMode] = useState("owned");
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [tradeData, setTradeData] = useState({ result: [], search: "" });
  const [copySuccess, setCopySuccess] = useState(false);
  const [language, setLanguage] = useState("de");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        const [userRes, statsRes] = await Promise.all([
          axios.get("/api/users/me", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("/api/users/pokedex-stats", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setUsername(userRes.data.username);
        setPokedexStats(statsRes.data);
      } catch (error) {
        console.error("Fehler beim Laden der Daten", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchTradeData = async () => {
      try {
        const token = localStorage.getItem("token");
        const query = new URLSearchParams();
        query.append("type", tradeMode);
        if (selectedFilter) query.append("filter", selectedFilter);

        const res = await axios.get(
          `/api/users/trade-helper?${query.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setTradeData(res.data);
      } catch (error) {
        console.error("Fehler beim Laden der Tauschdaten:", error);
      }
    };

    fetchTradeData();
  }, [selectedFilter, tradeMode]);

  const handleCopy = async () => {
    const ids = tradeData.result.join(",");
    const condition = selectedFilter ? FILTER_WORDS[selectedFilter]?.[language] : null;
    const searchString = ids + (condition ? `&${condition}` : "");

    try {
      await navigator.clipboard.writeText(searchString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      alert("Fehler beim Kopieren");
    }
  };

  const handleFilterToggle = (event, newFilter) => {
    setSelectedFilter(newFilter);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box mt={5} mx={2}>
      <Typography variant="h4" align="center" sx={{ color: theme.palette.text.primary, mb: 4 }}>
        Hallo {username} 👋
      </Typography>

      <ProfileGameStats />

      {/* Pokédex Statistik */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom align="center">
          Pokédex - Statistik
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {pokedexStats.length > 0 && (
          <>
            <Tabs
              value={selectedGen}
              onChange={(e, newValue) => setSelectedGen(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }}
            >
              {pokedexStats.map((gen, index) => (
                <Tab key={gen.generation} label={`GEN ${gen.generation}`} value={index} />
              ))}
            </Tabs>

            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 3,
                backgroundColor: theme.palette.background.paper,
                boxShadow: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Gesamt: {pokedexStats[selectedGen].total_in_generation} Pokémon
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                <Typography>Gefangen:</Typography>
                <Typography>{pokedexStats[selectedGen].catched}</Typography>
                <Typography>Shiny:</Typography>
                <Typography>{pokedexStats[selectedGen].shiny}</Typography>
                <Typography>Perfekt:</Typography>
                <Typography>{pokedexStats[selectedGen].perfect}</Typography>
                <Typography>Crypto:</Typography>
                <Typography>{pokedexStats[selectedGen].crypto}</Typography>
                <Typography>Dynamax:</Typography>
                <Typography>{pokedexStats[selectedGen].dynamax}</Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Tauschhelfer */}
      <Box mb={6}>
        <Typography variant="h5" gutterBottom align="center">
          Tauschhelfer
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Card
          variant="outlined"
          sx={{
            p: 3,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 2,
            maxWidth: 500,
            mx: "auto",
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Sprache: {language === "de" ? "Deutsch" : "English"}
              </Typography>
              <FormControlLabel
                control={<Switch checked={language === "en"} onChange={() => setLanguage((prev) => (prev === "de" ? "en" : "de"))} />}
                label={<TranslateIcon />}
              />
            </Stack>

            <Tabs
              value={tradeMode}
              onChange={(e, newValue) => setTradeMode(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              <Tab label="Pokémon im Besitz" value="owned" />
              <Tab label="Fehlende Pokémon" value="missing" />
            </Tabs>

            <Grid container spacing={1} justifyContent="center" sx={{ mb: 2 }}>
              {Object.keys(FILTERS).map((key) => (
                <Grid item xs={6} key={key}>
                  <ToggleButtonGroup
                    color="secondary"
                    value={selectedFilter === key ? key : null}
                    exclusive
                    onChange={() => setSelectedFilter(selectedFilter === key ? null : key)}
                    fullWidth
                  >
                    <ToggleButton value={key}>{FILTERS[key][language]}</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              ))}
            </Grid>

            <Box display="flex" justifyContent="center" mb={1}>
              <Tooltip title="Suchstring kopieren">
                <Button
                  variant="contained"
                  color={copySuccess ? "success" : "primary"}
                  onClick={handleCopy}
                >
                  {copySuccess ? "Kopiert!" : "Suchstring kopieren"}
                </Button>
              </Tooltip>
            </Box>

            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
              {tradeData.result?.length || 0} Pokémon gefunden
            </Typography>
            <Typography variant="caption" align="center" display="block" sx={{ mt: 1 }}>
              Pokémon GO erlaubt keine logische Kombination aus den einzelnen Suchmöglichkeiten.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Profile;
